import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useClientChecklists, ChecklistItem, useInfiniteChecklists } from '../lib/api';
import { useWorkflowStore } from '../store/useWorkflowStore';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { debounce } from '../lib/utils';

/**
 * ChecklistSelector component
 * 
 * A component for selecting checklists with search functionality.
 * 
 * @returns {JSX.Element} The ChecklistSelector component
 */
const ChecklistSelector: React.FC = () => {
  // Get the store state and actions
  const {
    selectedChecklists,
    checklistSearchTerm,
    setSelectedChecklists,
    addSelectedChecklist,
    removeSelectedChecklist,
    clearSelectedChecklists,
    setChecklistSearchTerm,
    selectedClient,
    selectedFacilityId,
    selectedUseCaseId
  } = useWorkflowStore();

  // Local state for search input (to avoid too many API calls)
  const [searchInput, setSearchInput] = useState(checklistSearchTerm);
  
  // Ref for the scroll container
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  // Get the checklists data with infinite scrolling
  const {
    data: infiniteData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error
  } = useInfiniteChecklists(checklistSearchTerm, selectedClient, selectedFacilityId, selectedUseCaseId);
  
  // Add scroll event listener to load more checklists when scrolling to the bottom
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;
    
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      // If scrolled to bottom (with a small threshold)
      if (scrollTop + clientHeight >= scrollHeight - 20) {
        if (hasNextPage && !isFetchingNextPage) {
          console.log('Fetching next page of checklists...');
          fetchNextPage();
        }
      }
    };
    
    scrollContainer.addEventListener('scroll', handleScroll);
    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);
  
  // Extract and flatten all checklists from all pages
  const checklists = infiniteData?.pages.flatMap(page => page.data) || [];
  
  // Debounced search function
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setChecklistSearchTerm(value);
    }, 300),
    [setChecklistSearchTerm]
  );
  
  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);
    debouncedSearch(value);
  };
  
  // Handle checkbox change
  const handleCheckboxChange = (checklist: ChecklistItem, checked: boolean) => {
    if (checked) {
      addSelectedChecklist(checklist);
    } else {
      removeSelectedChecklist(checklist.id);
    }
  };
  
  // Handle clear all button click
  const handleClearAll = () => {
    clearSelectedChecklists();
  };
  
  // Check if a checklist is selected
  const isSelected = (id: string) => {
    return selectedChecklists.some((checklist) => checklist.id === id);
  };
  
  // Use the checklists directly since filtering is done on the server
  const filteredChecklists = checklists;
  
  return (
    <div className="flex flex-col space-y-4">
      {/* Search and Selected Count */}
      <div className="flex items-center space-x-2">
        <div className="relative flex-grow">
          <Input
            type="text"
            placeholder="Search by checklist code..."
            value={searchInput}
            onChange={handleSearchChange}
            className="pr-10"
          />
          {searchInput && (
            <button
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              onClick={() => {
                setSearchInput('');
                setChecklistSearchTerm('');
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-500">
            {selectedChecklists.length} selected
          </span>
          {selectedChecklists.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearAll}
              className="text-xs h-8"
            >
              Clear All
            </Button>
          )}
        </div>
      </div>
      
      {/* Selected Checklists */}
      {selectedChecklists.length > 0 && (
        <div className="flex flex-wrap gap-2 p-2 border border-gray-200 rounded-md bg-gray-50">
          {selectedChecklists.map((checklist) => (
            <Badge
              key={checklist.id}
              variant="secondary"
              className="flex items-center gap-1 px-2 py-1"
            >
              <span className="max-w-[150px] truncate">{checklist.name}</span>
              <button
                onClick={() => removeSelectedChecklist(checklist.id)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-3 w-3"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </Badge>
          ))}
        </div>
      )}
      
      {/* Checklist List */}
      <div
        ref={scrollContainerRef}
        className="border border-gray-200 rounded-md overflow-y-auto h-[300px]"
      >
        {isLoading && (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        )}
        
        {isError && (
          <div className="flex flex-col items-center justify-center h-full p-4 text-center">
            <p className="text-red-500 mb-2">Error loading checklists</p>
            <p className="text-sm text-gray-500">
              {error instanceof Error ? error.message : 'Unknown error'}
            </p>
          </div>
        )}
        
        {!isLoading && !isError && filteredChecklists.length === 0 && (
          <div className="flex items-center justify-center h-full p-4 text-center">
            <p className="text-gray-500">
              {checklists.length === 0 
                ? "No checklists available" 
                : "No checklists match your search"}
            </p>
          </div>
        )}
        
        {!isLoading && !isError && filteredChecklists.length > 0 && (
          <ul className="divide-y divide-gray-200 overflow-x-auto">
            {filteredChecklists.map((checklist: ChecklistItem) => (
              <li
                key={checklist.id}
                className="p-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id={`checklist-${checklist.id}`}
                    checked={isSelected(checklist.id)}
                    onCheckedChange={(checked) =>
                      handleCheckboxChange(checklist, checked === true)
                    }
                  />
                  <div className="flex-grow min-w-0 overflow-hidden">
                    <label
                      htmlFor={`checklist-${checklist.id}`}
                      className="block cursor-pointer w-full"
                    >
                      <div className="font-medium pb-1 pr-2">{checklist.name}</div>
                      <div className="text-sm text-gray-500 flex items-center space-x-2">
                        <span className="truncate">{checklist.code}</span>
                        <span className="inline-block h-1 w-1 rounded-full bg-gray-300"></span>
                        <span>v{checklist.version}</span>
                      </div>
                    </label>
                  </div>
                </div>
              </li>
            ))}
            
            {/* Loading indicator for next page */}
            {isFetchingNextPage && (
              <li className="p-3 flex justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-blue-500"></div>
              </li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ChecklistSelector;
